import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { DomSanitizer, SafeHtml } from '../../../../../../node_modules/@angular/platform-browser';
import { BackendService } from '../../../../core/backend.service';
import { ApiResult } from '../../../../core/messages/api-result';
import { DisplayMessage } from '../../../../core/messages/display-message';
import { DrawingInspectionDetails } from '../../../../viewmodels/idms/drawing-inspection-details';
import { InspectionPoint } from '../../../../viewmodels/idms/inspection-point';
import { DrawingEquipmentData } from '../../../../viewmodels/idms/inspection-point-equipment-data';
import { DrawingPreview } from '../../../../viewmodels/media/preview/drawing-preview';
import { MediaItemService } from '../../media-item.service';

@Injectable()
export class DrawingPreviewService {
  inspectionPointAdded = new Subject<InspectionPoint>();
  inspectionPointDeleted = new Subject<InspectionPoint>();
  svg = new Subject<SafeHtml>();
  drawingInspectionDetails = new Subject<DrawingInspectionDetails>();
  loadingDrawingEquipmentData = new Subject<DrawingEquipmentData>();
  drawingEquipmentData = new BehaviorSubject<DrawingEquipmentData>(null);
  errors = new Subject<DisplayMessage[]>();

  constructor(private backendService: BackendService, private sanitizer: DomSanitizer, private mediaItemService: MediaItemService) {}

  addInspectionPoint(inspectionPoint: InspectionPoint) {
    this.inspectionPointAdded.next(inspectionPoint);
  }

  updateInspectionPoint(inspectionPoint: InspectionPoint) {
    this.inspectionPointAdded.next(inspectionPoint);
  }

  deleteInspectionPoint(inspectionPoint: InspectionPoint) {
    this.inspectionPointDeleted.next(inspectionPoint);
  }

  loadDrawing(mediaItemDetailsId: string) {
    this.loadPreview(mediaItemDetailsId);
  }

  saveDetails(details: DrawingInspectionDetails) {
    this.backendService.update<DrawingInspectionDetails>(DrawingInspectionDetails, details.mediaItemDetailsId, details).subscribe(
      foundDetails => {
        if (foundDetails) {
          this.drawingInspectionDetails.next(foundDetails);
        }
      },
      (errorResult: ApiResult) => {
        this.handleErrors('The drawing details could not be saved', errorResult);
      },
    );
  }

  loadEquipmentData(drawingInspectionDetails: DrawingInspectionDetails) {
    const drawingEquipmentData = new DrawingEquipmentData();
    drawingEquipmentData.mediaItemDetailsId = drawingInspectionDetails.mediaItemDetailsId;
    drawingEquipmentData.selectedInspectionPoints = drawingInspectionDetails.inspectionPoints;

    this.loadingDrawingEquipmentData.next();

    this.backendService
      .update<DrawingEquipmentData>(DrawingEquipmentData, drawingEquipmentData.mediaItemDetailsId, drawingEquipmentData, false)
      .subscribe(
        equipmentData => {
          if (equipmentData) {
            this.drawingEquipmentData.next(equipmentData);
          }
        },
        (errorResult: ApiResult) => {
          this.handleErrors('The equipment data could not be loaded', errorResult);
        },
      );
  }

  public loadPreview(mediaItemDetailsId: string) {
    this.backendService.getById<DrawingPreview>(DrawingPreview, mediaItemDetailsId).subscribe(drawingPreview => {
      if (drawingPreview && drawingPreview.preview) {
        this.svg.next(this.sanitizer.bypassSecurityTrustHtml(drawingPreview.preview));
        this.mediaItemService.setAllowEditDrawingStatus(true);

        this.loadInspectionDetails(mediaItemDetailsId, drawingPreview);
      }
    });
  }

  private loadInspectionDetails(mediaItemDetailsId: string, drawingPreview: DrawingPreview) {
    this.backendService.getById<DrawingInspectionDetails>(DrawingInspectionDetails, mediaItemDetailsId).subscribe(
      details => {
        if (details) {
          details.fileLastModified = drawingPreview.lastModified;
          this.drawingInspectionDetails.next(details);

          this.loadEquipmentData(details);
        }
      },
      (errorResult: ApiResult) => {
        this.handleErrors('The drawing details could not be loaded', errorResult);
      },
    );
  }

  private handleErrors(summary: string, errorResult: ApiResult) {
    const errors: DisplayMessage[] = [];
    errorResult.messages.forEach(message => {
      errors.push(new DisplayMessage(summary, message.text, message.severity));
    });
    this.errors.next(errors);
  }
}
